import { createContext, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import type { AssessmentV2State, Part1Answers } from "./types";
import { allScreensFor } from "./chapters";
import { DEFAULT_BAND, storageKey, type Band } from "./band";
import { clearSession, ensureSession, flushOutbox, recordAnswer } from "./persistence";
import { cloudStorage, isCloudStateReady, subscribeCloudState } from "@/lib/cloudStore";


function nowIso() { return new Date().toISOString(); }

function initial(band: Band): AssessmentV2State {
  return {
    band,
    currentScreen: "__intro__",
    part1: {},
    part2: {},
    startedAt: nowIso(),
    updatedAt: nowIso(),
  };
}

function load(band: Band): AssessmentV2State {
  try {
    const raw = cloudStorage.getItem(storageKey(band));
    if (!raw) return initial(band);
    const parsed = JSON.parse(raw) as AssessmentV2State;
    // A saved run always belongs to the band it was saved under.
    parsed.band = band;
    // Guard: unknown screen id → snap back to intro.
    const known = parsed.currentScreen === "__intro__"
      || parsed.currentScreen === "__end_part1__"
      || allScreensFor(band).includes(parsed.currentScreen);
    if (!known) parsed.currentScreen = "__intro__";
    return parsed;
  } catch { return initial(band); }
}

type Action =
  | { type: "goto"; screen: string }
  | { type: "set_part1"; patch: Partial<Part1Answers> }
  | { type: "set_part2"; key: string; value: unknown }
  | { type: "hydrate"; state: AssessmentV2State }
  | { type: "reset" };

function reducer(state: AssessmentV2State, action: Action): AssessmentV2State {
  switch (action.type) {
    case "goto":
      return { ...state, currentScreen: action.screen, updatedAt: nowIso() };
    case "set_part1":
      return { ...state, part1: { ...state.part1, ...action.patch }, updatedAt: nowIso() };
    case "set_part2":
      return { ...state, part2: { ...state.part2, [action.key]: action.value }, updatedAt: nowIso() };
    case "hydrate":
      // The saved run just arrived from the cloud. It wins over the empty
      // shell we started with — but never over answers given since mount.
      if (state.currentScreen !== "__intro__"
        || Object.keys(state.part1).length > 0
        || Object.keys(state.part2).length > 0) return state;
      return action.state;
    case "reset":
      return initial(state.band);
  }
}


/**
 * Which Part 1 screen owns each answer field. Persistence is keyed on the
 * screen a learner actually saw, so a replay reconstructs the same walk.
 * A field with no screen here is derived, not answered.
 */
const PART1_FIELD_SCREEN: Record<string, string> = {
  qualification: "p1_qualification",
  study_appetite: "p1_study_appetite",
  reality_check: "p1_reality_check",
  salary_floor: "p1_salary_floor",
  salary_flex: "p1_salary_flex",
  years_experience: "p1_experience_location",
  work_location: "p1_experience_location",
  professional_field: "p1_background",
  career_intent: "p1_background",
  stream: "p1_stream",
  stream_lean: "p1_stream_lean",
  money_matters: "p1_money_matters",
  study_mobility: "p1_study_mobility",
  school_year: "p1_school_year",
  what_you_like: "p1_what_you_like",
  passion_ids: "px_passions",
  childhood_dream: "px_childhood_dream",
  career_pull_ids: "px_career_pull",
  hobby_frequency: "px_hobby_frequency",
  cause_rank_ids: "px_cause_rank",
  cause_roles: "px_cause_role",
  cause_other: "px_cause_rank",
};

/** Part 2 keys that are aggregates of other screens, not answers themselves. */
const PART2_DERIVED_KEYS = new Set([
  "slots",
  "archetype_tally",
  // The ported v1 instrument accumulates across its block screens, so these
  // two maps are aggregates of many screens rather than one screen's answer.
  // Each block still records its own evidence row under its screen id.
  "v1_trait_answers",
  "v1_quiz_answers",
]);

interface Ctx {
  state: AssessmentV2State;
  band: Band;
  goto: (screen: string) => void;
  setPart1: (patch: Partial<Part1Answers>) => void;
  setPart2: (key: string, value: unknown) => void;
  reset: () => void;
}

const AssessmentV2Ctx = createContext<Ctx | null>(null);

export function AssessmentV2Provider({
  children,
  band = DEFAULT_BAND,
}: {
  children: React.ReactNode;
  band?: Band;
}) {
  const [state, dispatch] = useReducer(reducer, band, load);
  // The run lives in the cloud, so nothing is written back until this
  // learner's record has arrived — otherwise an empty shell could overwrite
  // a walk they started on another device.
  const [hydrated, setHydrated] = useState(isCloudStateReady());

  useEffect(() => {
    if (hydrated) return;
    const check = () => {
      if (!isCloudStateReady()) return;
      dispatch({ type: "hydrate", state: load(band) });
      setHydrated(true);
    };
    check();
    return subscribeCloudState(check);
  }, [hydrated, band]);

  useEffect(() => {
    if (!hydrated) return;
    try { cloudStorage.setItem(storageKey(state.band), JSON.stringify(state)); } catch { /* ignore */ }
  }, [state, hydrated]);


  // Any answers stranded by a dropped connection or a closed tab go out
  // again as soon as the flow is mounted.
  useEffect(() => {
    void flushOutbox(state.band);
  }, [state.band]);

  /**
   * The two aggregates a screen builds as it answers — the slot state and the
   * archetype tally — kept in a ref so they can be attached to the answer row.
   *
   * A screen writes its raw answer and its aggregates in the same handler, and
   * `state` inside this closure is one render behind, so reading the aggregates
   * from state persisted the *previous* screen's totals: the final screen's
   * contribution never reached the database and no replay could rebuild the
   * archetypes. The ref is updated synchronously, and recording is deferred to
   * a microtask so every write in the handler is in before the row is queued.
   */
  const derivedRef = useRef<{ slots: Record<string, unknown>; tally: Record<string, unknown> }>({
    slots: {},
    tally: {},
  });
  useEffect(() => {
    derivedRef.current = {
      slots: (state.part2.slots as Record<string, unknown>) ?? derivedRef.current.slots,
      tally: (state.part2.archetype_tally as Record<string, unknown>) ?? derivedRef.current.tally,
    };
  }, [state.part2]);


  const value = useMemo<Ctx>(() => {
    const sequenceOf = (screenId: string) => {
      const idx = allScreensFor(state.band).indexOf(screenId);
      return idx < 0 ? null : idx;
    };

    return {
      state,
      band: state.band,
      goto: (screen) => dispatch({ type: "goto", screen }),
      setPart1: (patch) => {
        dispatch({ type: "set_part1", patch });
        // One row per screen, carrying every field that screen owns.
        const byScreen = new Map<string, Record<string, unknown>>();
        for (const [field, val] of Object.entries(patch)) {
          const screenId = PART1_FIELD_SCREEN[field];
          if (!screenId) continue;
          byScreen.set(screenId, { ...(byScreen.get(screenId) ?? {}), [field]: val });
        }
        for (const [screenId, fields] of byScreen) {
          recordAnswer({
            band: state.band,
            part: "part1",
            screenId,
            rawValue: fields,
            sequence: sequenceOf(screenId),
          });
        }
      },
      setPart2: (key, value) => {
        dispatch({ type: "set_part2", key, value });
        if (PART2_DERIVED_KEYS.has(key)) {
          // Derived aggregates are not answers, but they are evidence of the
          // walk: they ride along with the next answer row so the run can be
          // rebuilt on any device.
          if (key === "slots") derivedRef.current.slots = (value as Record<string, unknown>) ?? {};
          if (key === "archetype_tally") {
            derivedRef.current.tally = (value as Record<string, unknown>) ?? {};
          }
          return;
        }
        const band = state.band;
        const sequence = sequenceOf(key);
        queueMicrotask(() => {
          const { slots, tally } = derivedRef.current;
          recordAnswer({
            band,
            part: "part2",
            screenId: key,
            rawValue: value,
            slotWrites: Object.keys(tally).length > 0 ? { ...slots, archetype_tally: tally } : slots,
            sequence,
          });
        });
      },
      reset: () => {
        try { cloudStorage.removeItem(storageKey(state.band)); } catch { /* */ }
        clearSession(state.band);
        dispatch({ type: "reset" });
      },
    };
  }, [state]);

  return <AssessmentV2Ctx.Provider value={value}>{children}</AssessmentV2Ctx.Provider>;
}

export function useAssessmentV2(): Ctx {
  const ctx = useContext(AssessmentV2Ctx);
  if (!ctx) throw new Error("useAssessmentV2 must be used inside AssessmentV2Provider");
  return ctx;
}

/** Band, readable from any screen without prop-drilling. */
export function useBand(): Band {
  return useAssessmentV2().band;
}

/**
 * Create the durable session for this run. Called once the learner starts
 * answering — not on mount, so a bounce off the intro screen never leaves
 * an empty session behind.
 */
export function useEnsureSession(band: Band, active: boolean) {
  useEffect(() => {
    if (!active) return;
    void ensureSession(band);
  }, [band, active]);
}
